import * as React from "react"

import * as RadioGroupPrimitive from "@radix-ui/react-radio-group"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "../../cn"

const radioGroupVariants = cva(["grid gap-2"])

const radioGroupItemVariants = cva([
  "relative size-4 shrink-0 cursor-pointer rounded-full",
  "border border-coval-border-control",
  "bg-surface-default",
  "transition-colors duration-150",
  "disabled:cursor-default disabled:opacity-50",
  "data-[state=checked]:border-coval-contrast",
])

/* ── RadioGroup ────────────────────────────────────────────── */

export interface RadioGroupProps
  extends
    React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>,
    VariantProps<typeof radioGroupVariants> {}

const RadioGroup = React.forwardRef<
  React.ComponentRef<typeof RadioGroupPrimitive.Root>,
  RadioGroupProps
>(({ className, ...props }, ref) => (
  <RadioGroupPrimitive.Root
    ref={ref}
    className={cn(radioGroupVariants(), className)}
    {...props}
  />
))
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName

/* ── RadioGroupItem ────────────────────────────────────────── */

export interface RadioGroupItemProps
  extends
    React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>,
    VariantProps<typeof radioGroupItemVariants> {}

const RadioGroupItem = React.forwardRef<
  React.ComponentRef<typeof RadioGroupPrimitive.Item>,
  RadioGroupItemProps
>(({ className, ...props }, ref) => (
  <RadioGroupPrimitive.Item
    ref={ref}
    className={cn(radioGroupItemVariants(), className)}
    {...props}
  >
    <RadioGroupPrimitive.Indicator asChild>
      <svg
        className="absolute inset-0 m-auto"
        width="8"
        height="8"
        viewBox="0 0 8 8"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <circle cx="4" cy="4" r="3" className="fill-coval-contrast" />
      </svg>
    </RadioGroupPrimitive.Indicator>
  </RadioGroupPrimitive.Item>
))
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName

export {
  RadioGroup,
  RadioGroupItem,
  radioGroupVariants,
  radioGroupItemVariants,
}
